import { addDriverFromTrip, fatchLocationSimApi, fecthTripLoaction, updateDriverConsentApi } from '_services'
import { UserContext } from 'context'
import { actionPermission, utils } from 'helper'
import { AddEditMarketDriver, AddEditOwnDriver, DriverSelectModal, EbillExtentionModal } from 'modals'
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import { FaCheckCircle, FaMapMarkerAlt, FaClock, FaUser, FaPhone } from 'react-icons/fa'
import StorageService from 'utils/StorageServices'
import {
    GoogleMap,
    Marker,
    InfoWindow,
    DirectionsService,
    DirectionsRenderer,
    Polyline,
} from "@react-google-maps/api";
import { useGoogleMaps } from "components/GoogleMapsProvider/GoogleMapsProvider";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { constant } from 'constants';
import './simtrack.css'
import useDocumentTitle from 'hooks/useDocumentTitle'

const SimTracking = ({
    callApi,
}) => {
    const { globTripDetails } = useContext(UserContext)
    const [driverModalShow, setDriverModalShow] = useState(false);
    const [driverDetails, setDriverDetails] = useState({
        driver: ""
    })
    useDocumentTitle("SIM Tracking | T360");
    const [loadingDriver, setLoadingDriver] = useState(false)
    const [loadingConsent, setLoadingConsent] = useState(false)
    const [showOffcanvasDriver, setShowOffcanvasDriver] = useState(false)
    const [showOffcanvasOwnDriver, setShowOffcanvasOwnDriver] = useState(false)

    const [fatchLoading, setFatchLoading] = useState(false)
    const [tripLocation, setTripLocation] = useState([])
    const [countdown, setCountdown] = useState(0);

    // Map State - Fixed and optimized
    const [activeMarker, setActiveMarker] = useState(null);
    const isLoaded = useGoogleMaps();
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const clustererRef = useRef(null);

    const [directionsMap, setDirectionsMap] = useState({});
    const [requestedDirections, setRequestedDirections] = useState({});
    const [directionsResponse, setDirectionsResponse] = useState(null);
    const [directionsError, setDirectionsError] = useState(null);
    const [driverModal, setDriverModal] = useState(false);

    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        messageType: null,
        templateId: null,
        templateData: {}
    });


    // Clear directions when marker changes
    useEffect(() => {
        setDirectionsMap({});
        setRequestedDirections({});
    }, [activeMarker]);

    const driverId = globTripDetails?.sim_tracking_id


    useEffect(() => {
        getTripLoaction()
    }, [])

    const containerStyle = {
        width: "100%",
        height: "calc(100% - 84px)",
        minHeight: "422px"
    };

    const getValidCoordinates = useCallback((vehicle) => {

        const coords = vehicle?.location?.coordinates;

        if (!coords || !Array.isArray(coords) || coords.length < 2) {
            console.warn('Invalid coordinates for vehicle:', vehicle);
            return null;
        }

        let lat = parseFloat(coords[0]);
        let lng = parseFloat(coords[1]);

        if (isNaN(lat) || isNaN(lng)) {
            console.warn('Invalid coordinate values:', coords);
            return null;
        }

        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            console.warn('Coordinates out of valid range:', { lat, lng });
            return null;
        }

        if (Math.abs(lat) > Math.abs(lng) && Math.abs(lng) <= 90) {
            console.warn('Swapping coordinates - detected [lng, lat] format');
            [lat, lng] = [lng, lat];
        }

        return { lat, lng };
    }, []);

    const firstVehicleWithGPS = useMemo(() => {
        const validVehicles = tripLocation?.trip_location?.filter(vehicle => {
            const coords = getValidCoordinates(vehicle);
            return coords !== null;
        });

        return validVehicles?.[0] || null;
    }, [tripLocation?.trip_location, getValidCoordinates]);

    const center = useMemo(() => {
        if (firstVehicleWithGPS) {
            const coords = getValidCoordinates(firstVehicleWithGPS);
            if (coords) {
                return coords;
            }
        }

        return { lat: 20.5937, lng: 78.9629 };
    }, [firstVehicleWithGPS, getValidCoordinates]);

    useEffect(() => {
        if (!mapRef.current || !window.google || !tripLocation?.trip_location?.length) {
            return;
        }
        getDirections()
        markersRef.current.forEach(marker => {
            marker.setMap(null);
        });
        markersRef.current = [];

        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
        }

        const newMarkers = tripLocation.trip_location.map((vehicle, index) => {
            const coords = getValidCoordinates(vehicle);

            if (!coords) {
                console.warn(`Skipping vehicle ${index} - invalid coordinates:`, vehicle.location?.coordinates);
                return null;
            }

            const stepNumber = tripLocation.trip_location.length - index;
            const isActive = activeMarker?.id === vehicle._id;
            const isLastIndex = index === 0;

            const marker = new window.google.maps.Marker({
                position: coords,
                map: mapRef.current,
                icon: {
                    url: isLastIndex ? "https://img.icons8.com/?size=192&id=afVt8guq75IH&format=png" : createSVGCircleIcon(stepNumber, isActive),
                    scaledSize: new window.google.maps.Size(32, 32),
                    anchor: new window.google.maps.Point(16, 16),
                },
                title: `Location ${index + 1}: ${vehicle.terminal_location || 'Unknown location'}`,
                zIndex: 1000 + index,
            });

            marker.addListener("click", () => {
                setActiveMarker({
                    id: vehicle._id,
                    position: coords,
                    gps: {
                        ...vehicle.location?.coordinates,
                        dttime: vehicle.createdAt ? utils.formatDateMonth(vehicle.createdAt) : vehicle.dttime,
                        location: vehicle.terminal_location
                    },
                    vehicle,
                });
            });

            return marker;
        }).filter(Boolean);

        markersRef.current = newMarkers;

        if (newMarkers.length > 1) {
            clustererRef.current = new MarkerClusterer({
                map: mapRef.current,
                markers: newMarkers,
                gridSize: 60,
                maxZoom: 15,
            });
        }

        if (newMarkers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            newMarkers.forEach(marker => {
                bounds.extend(marker.getPosition());
            });

            if (newMarkers.length === 1) {
                mapRef.current.setCenter(newMarkers[0].getPosition());
                mapRef.current.setZoom(15);
            } else {
                mapRef.current.fitBounds(bounds, { padding: 50 });
            }
        }

    }, [tripLocation?.trip_location, getValidCoordinates]);


    const createSVGCircleIcon = useCallback((stepNumber, isActive = false) => {
        const circleColor = isActive ? '#ff4444' : '#007bff';

        const svg = `
        <svg width="50" height="50" xmlns="http://www.w3.org/2000/svg">
            <!-- Circle with number -->
            <circle cx="25" cy="25" r="20" fill="blue" stroke="${circleColor}" stroke-width="3"/>
            <text x="25" y="30" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="white">${stepNumber}</text>
        </svg>
    `;

        return 'data:image/svg+xml;base64,' + btoa(svg);
    }, []);

    const getDirections = useCallback(async () => {
        if (!tripLocation?.trip_location || tripLocation.trip_location.length < 2) {
            return;
        }

        const validCoordinates = tripLocation.trip_location
            .map(vehicle => getValidCoordinates(vehicle))
            .filter(Boolean);

        if (validCoordinates.length < 2) {
            console.warn('Not enough valid coordinates for directions');
            return;
        }

        try {
            const directionsService = new window.google.maps.DirectionsService();

            const origin = validCoordinates[0];
            const destination = validCoordinates[validCoordinates.length - 1];
            const waypoints = validCoordinates.slice(1, -1).map(coord => ({
                location: coord,
                stopover: true
            }));

            const request = {
                origin: origin,
                destination: destination,
                waypoints: waypoints,
                travelMode: window.google.maps.TravelMode.DRIVING,
                optimizeWaypoints: false,
                avoidHighways: false,
                avoidTolls: false
            };

            await directionsService.route(request, (result, status) => {
                if (status === 'OK') {
                    setDirectionsResponse(result);
                    setDirectionsError(null);
                } else {
                    setDirectionsError(status);
                }
            });

        } catch (error) {
            console.error('Error getting directions:', error);
            setDirectionsError(error.message);
        }
    }, [tripLocation]);

    const DetailBox = ({ vehicle, gps }) => {
        const coords = getValidCoordinates(vehicle);

        return (
            <div style={{ minWidth: 300, maxWidth: 300, padding: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <FaMapMarkerAlt style={{ color: '#007bff', marginRight: '8px' }} />
                    <h6 style={{ margin: 0 }}>Location Details</h6>
                </div>

                <div style={{ marginBottom: '10px' }}>
                    <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>Driver:</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{driverId?.name || 'N/A'}</p>
                </div>

                <div style={{ marginBottom: '10px' }}>
                    <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>Last Updated:</p>
                    <p style={{ margin: 0 }}>{gps.dttime}</p>
                </div>

                <hr style={{ margin: '10px 0' }} />

                <div style={{ marginBottom: '10px' }}>
                    <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>Location:</p>
                    <p style={{ margin: 0, fontSize: '14px' }}>{gps?.location || 'Location not available'}</p>
                </div>

                {coords && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                        <span>Lat: {coords.lat.toFixed(6)}</span>
                        <span>Lng: {coords.lng.toFixed(6)}</span>
                    </div>
                )}
            </div>
        )
    }

    const handleAddDriver = async () => {
        if (!driverDetails?.driver?._id) {
            toast.error('Please select a driver');
            return;
        }

        try {
            setLoadingDriver(true);
            const payload = {
                company_id: StorageService?.getCompanyId(),
                trip_id: globTripDetails?._id,
                driver_id: driverDetails?.driver?._id,
                is_existing_driver: 0
            };

            const res = await addDriverFromTrip(payload);

            if (res?.responseCode === 200) {
                toast.success(res?.responseMessage);
                callApi();
                setDriverModalShow(false);
                setCountdown(0)
            } else {
                toast.error(res?.responseMessage || 'Failed to add driver');
            }
        } catch (error) {
            toast.error(error?.message || 'An error occurred');
        } finally {
            setLoadingDriver(false);
        }
    };

    const callBackDriver = (data) => {
        setDriverDetails((pre) => ({ driver: { ...data, name: data?.name, id: data?._id } }))
        callApi();
    }

    const nextModal = () => {
        setDriverModalShow(false)
        if (globTripDetails?.vehicle_ownership == 2) {
            setShowOffcanvasDriver(true)
        } else {
            setShowOffcanvasOwnDriver(true)
        }


    }

    let driverModalProps = {
        isOpen: driverModalShow,
        closeModal: () => {
            setDriverDetails({ driver: "" })
            setDriverModalShow(false)
        },
        modalMainClass: "_dashCommon_Modal _smModal",
        modalTitle: "Driver List",
        submitTitle: "Submit",
        onSubmit: handleAddDriver,
        setDriverDetails: setDriverDetails,
        driverDetails: driverDetails,
        isLoadding: loadingDriver,
        nextModal: nextModal
    }

    const updateDriverConsentHandle = async () => {
        if (countdown > 0) {
            const minutes = Math.floor(countdown / 60);
            const seconds = countdown % 60;
            toast.info(`Please wait ${minutes > 0 ? `${minutes} min ${seconds} sec` : `${seconds} sec`} before trying again`);
            return;
        }

        setLoadingConsent(true);
        const payload = {
            company_id: StorageService.getCompanyId(),
            driver_id: driverId?._id
        };

        try {
            const res = await updateDriverConsentApi(payload);

            let countdownSeconds = 5 * 60; // default fallback 5 minutes

            if (res?.responseCode === 200) {
                toast.success(res?.responseMessage);

                if (res?.data?.minutes) {
                    countdownSeconds = parseInt(res.data.minutes) * 60;
                }

                setCountdown(countdownSeconds);
                let timerId = null;
                timerId = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(timerId);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);

                callApi();
            } else if (res?.responseCode === 400) {
                const timeMatch = res?.responseMessage?.match(/(\d+)\s*Min\(s\)/i);
                if (timeMatch?.[1]) {
                    countdownSeconds = parseInt(timeMatch[1]) * 60;
                }
            }



        } catch (error) {
            toast.error("An error occurred");
        } finally {
            setLoadingConsent(false);
        }
    };


    const getTripLoaction = async () => {
        const payload = {
            company_id: StorageService.getCompanyId(),
            trip_id: globTripDetails?._id
        }

        try {
            const res = await fecthTripLoaction(payload)
            if (res?.responseCode === 200) {
                setTripLocation(res?.data)
            }
        } catch (error) {
            console.error('Error fetching trip location:', error);
        }
    }

    const fatchLocationHandle = async () => {
        const payload = {
            company_id: StorageService?.getCompanyId(),
            driver_id: driverId?._id,
            trip_id: globTripDetails?._id
        }

        setFatchLoading(true)
        try {
            const res = await fatchLocationSimApi(payload)
            if (res?.responseCode === 200) {
                await getTripLoaction() // Refresh location data
                toast.success('Location fetched successfully');
            } else {
            }
        } catch (error) {
            // toast.error('Error fetching location');
        } finally {
            setFatchLoading(false)
        }
    }

    const formatCountdown = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    const handlePermissionCall = async (e) => {
        e.preventDefault();

        await actionPermission({
            company_id: StorageService.getCompanyId(),
            type: constant?.USABILITY_CONFIG_TYPES?.DRIVER_LOCATION_TRIP,
            onSuccess: () => fatchLocationHandle(),
            setModalConfig, // Pass setter
        });
    };

    let ebillExpProps = {
        isOpen: modalConfig.isOpen,
        closeModal: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
        onSubmit: (e) => setModalConfig(prev => ({ ...prev, isOpen: false })),
        modalMainClass: " _dashCommon_Modal __actionPermissionModal _smModal",
        headerPart: true,
        floatCrossBtn: true,
        submitTitle: "Send Enquiry",
        messageType: modalConfig.messageType,
        templateId: modalConfig.templateId,
        templateData: modalConfig.templateData
    };

    return (
        <>
            <div className='__simTrack_main'>
                {/* Step 1 - Driver Details */}
                <div className='__simTrack_otr'>
                    <div className='__simTrack_head'>
                        <p>Step 1 - Add Driver Details</p>
                        {driverId?._id && (
                            <div className="step-status-icon">
                                <FaCheckCircle style={{ color: '#28a745' }} />
                            </div>
                        )}
                    </div>
                    <div className='__simTrack_body'>
                        {!driverId?._id ? (
                            <button
                                className='anime-btn __addEdit_btn'
                                onClick={() => { setDriverModalShow(true); setDriverModal(true) }}
                                disabled={loadingDriver}
                            >
                                {loadingDriver ? 'Adding Driver...' : 'Add Driver'}
                            </button>
                        ) : (
                            <div className='__simTrack_driverDetails'>
                                <div className='__simDrverInfo'>
                                    <FaUser style={{ marginRight: '8px', color: '#007bff' }} />
                                    <div>
                                        <span>Driver Name</span>
                                        <p style={{ cursor: "pointer", textDecoration: "underline" }}
                                            onClick={() => { globTripDetails?.vehicle_ownership == 2 ? setShowOffcanvasDriver(driverId) : setShowOffcanvasOwnDriver(driverId); setDriverModal(false) }}
                                        >{driverId?.name}</p>
                                    </div>
                                </div>
                                {driverId?.contact_no && (
                                    <div className='__simDrverInfo'>
                                        <FaPhone style={{ marginRight: '8px', color: '#007bff' }} />
                                        <div>
                                            <span>Mobile Number</span>
                                            <p>{driverId?.contact_no}</p>
                                        </div>
                                    </div>
                                )}
                                {driverId?.name && <button className='anime-btn __addEdit_btn' onClick={() => { setDriverModalShow(true); setDriverModal(true); setDriverDetails((pre) => ({ driver: { ...driverId, name: driverId?.name, id: driverId?._id } })) }}>Change Driver</button>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 2 - Driver Consent */}
                {driverId?.contact_no && (
                    <div className='__simTrack_otr'>
                        <div className='__simTrack_head'>
                            <p>Step 2 - Driver Consent</p>
                            {driverId?.consent_status === 2 && (
                                <div className="step-status-icon">
                                    <FaCheckCircle style={{ color: '#28a745' }} />
                                </div>
                            )}
                        </div>
                        <div className='__simTrack_body'>
                            {!driverId?.consent_status && (
                                <div className='__simDriver_consentMain'>
                                    <div className='__simDriver_consentBx'>
                                        <p>Send Request for Driver consent</p>
                                        <button
                                            className='anime-btn __resendBtnEnable'
                                            disabled={loadingConsent || countdown > 0}
                                            onClick={updateDriverConsentHandle}
                                        >
                                            {loadingConsent ? "Sending..." : "Send Request"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {driverId?.consent_status === 1 && (
                                <div className='__simDriver_consentMain'>
                                    <div className='__simDriver_consentBx'>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                            <FaClock style={{ marginRight: '8px', color: '#ffc107' }} />
                                            <p style={{ margin: 0 }}>Driver consent request sent. Waiting for response</p>
                                        </div>
                                        {countdown === 0 ? (
                                            <button
                                                className='anime-btn __resendBtnEnable'
                                                disabled={loadingConsent}
                                                onClick={updateDriverConsentHandle}
                                            >
                                                {loadingConsent ? "Sending..." : "Resend Request"}
                                            </button>
                                        ) : (
                                            <div className="countdown-display">
                                                <p>Resend available in: {formatCountdown(countdown)}</p>
                                            </div>
                                        )}
                                    </div>

                                    {driverId?.consent_updated_at && <p className='__smD_time'>Last attempt made at {utils.formatDateMonth(driverId?.consent_updated_at)} | {utils.formatAMPM(driverId?.consent_updated_at)}</p>}
                                </div>
                            )}

                            {driverId?.consent_status === 2 && (
                                <div className='__consentRec_bx'>
                                    <span>
                                        <FaCheckCircle />
                                    </span>
                                    <p>Consent Received</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 3 - Driver Location */}
                {driverId?.consent_status === 2 && (
                    <div className='__simTrack_otr'>
                        <div className='__simTrack_head'>
                            <p>Step 3 - Driver Location</p>
                            <button
                                className='anime-btn fetchLoc-btn'
                                disabled={fatchLoading || (tripLocation?.trip?.location_fetch_count == 10)}
                                onClick={(e) => handlePermissionCall(e)}
                            >
                                {fatchLoading ? "Fetching..." : "Fetch Location"}
                            </button>
                            <p style={{ marginLeft: 'auto', fontSize: '14px', color: '#666' }}>
                                {`${Math.max(0, 10 - (tripLocation?.trip?.location_fetch_count || 0))}/10 Remaining`}
                            </p>
                        </div>
                        <div className='__simTrack_body __mainlockBx'>
                            <div className='__inrlockBx'>
                                {tripLocation?.trip_location?.length > 0 ? (
                                    tripLocation.trip_location.map((location, index) => {
                                        const coords = getValidCoordinates(location);
                                        return (
                                            <div key={location._id || index} className='__sim_driverLoc_bx'>
                                                <span className='_simCount' style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '20px',
                                                    height: '20px',
                                                    backgroundColor: '#007bff',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px',
                                                    flexShrink: 0,
                                                    minWidth: '20px',
                                                    minHeight: '20px'
                                                }}>{index + 1}</span>
                                                <div className='_simDetails'>
                                                    <p>{location?.terminal_location || 'Location not available'}</p>
                                                    <span>
                                                        {location?.createdAt
                                                            ? `${utils.formatDateMonth(location.createdAt)} | ${utils.formatAMPM(location.createdAt)}`
                                                            : 'Time not available'
                                                        } | User - {location?.created_by?.full_name || ''}
                                                    </span>

                                                </div>
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className="no-locations">
                                        <p>No locations fetched yet. Click "Fetch Location" to get driver's current location.</p>
                                    </div>
                                )}
                            </div>

                            <div className='__inrlockBx'>
                                <div>
                                    {!isLoaded ? (
                                        <div className="map-loading">Loading Map...</div>
                                    ) : (
                                        <GoogleMap
                                            mapContainerStyle={containerStyle}
                                            center={center}
                                            zoom={tripLocation?.trip_location?.length > 1 ? 8 : 15}
                                            onLoad={(map) => {
                                                mapRef.current = map;
                                            }}
                                            options={{
                                                mapTypeControl: true,
                                                fullscreenControl: true,
                                                streetViewControl: false,
                                                zoomControl: true,
                                                mapTypeId: 'roadmap',
                                                gestureHandling: 'cooperative'
                                            }}
                                        >
                                            {/* Debug: Show center as a small marker */}
                                            <Marker
                                                position={center}
                                                icon={{
                                                    url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Ccircle cx='5' cy='5' r='3' fill='red' stroke='white' stroke-width='1'/%3E%3C/svg%3E",
                                                    scaledSize: new window.google.maps.Size(10, 10),
                                                }}
                                                title="Map Center"
                                            />
                                            {directionsResponse && (
                                                <DirectionsRenderer
                                                    directions={directionsResponse}
                                                    options={{
                                                        suppressMarkers: true, // We'll use custom markers
                                                        polylineOptions: {
                                                            strokeColor: '#007bff',
                                                            strokeWeight: 4,
                                                            strokeOpacity: 0.8
                                                        }
                                                    }}
                                                />
                                            )}
                                            {!directionsResponse && directionsError && tripLocation?.trip_location?.length > 1 && (
                                                <Polyline
                                                    path={tripLocation.trip_location
                                                        .map(vehicle => getValidCoordinates(vehicle))
                                                        .filter(Boolean)
                                                    }
                                                    options={{
                                                        strokeColor: '#ff6b6b',
                                                        strokeOpacity: 0.8,
                                                        strokeWeight: 3,
                                                        strokeStyle: 'dashed',
                                                        icons: [{
                                                            icon: {
                                                                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                                                scale: 3,
                                                                strokeColor: '#ff6b6b'
                                                            },
                                                            offset: '100%'
                                                        }]
                                                    }}
                                                />
                                            )}

                                            {/* Info Window */}
                                            {activeMarker && (
                                                <InfoWindow
                                                    position={activeMarker.position}
                                                    onCloseClick={() => setActiveMarker(null)}
                                                >
                                                    <DetailBox
                                                        vehicle={activeMarker.vehicle}
                                                        gps={activeMarker.gps}
                                                    />
                                                </InfoWindow>
                                            )}
                                        </GoogleMap>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div >

            {/* Modals */}
            {
                driverModalShow && (
                    <DriverSelectModal {...driverModalProps} />
                )
            }

            <div
                className={`offcanvas offcanvas-end common_offCanvas ${showOffcanvasDriver ? "show" : ""}`}
                tabIndex={-1}
                style={{ visibility: showOffcanvasDriver ? "visible" : "hidden" }}
            >
                {showOffcanvasDriver && (
                    <AddEditMarketDriver
                        isOpen={showOffcanvasDriver}
                        onClose={() => {
                            if (driverModal) {
                                setShowOffcanvasDriver(false);
                                setDriverModalShow(true);
                            } else {
                                setShowOffcanvasDriver(false);
                                setDriverModalShow(false);
                            }
                        }}
                        driverModal={driverModal}
                        callBackDriver={callBackDriver}
                        hideAddNewOption={false}
                        branchFirmId={globTripDetails?.interfirm == 0 ? globTripDetails.firm_id : globTripDetails?.vehicle_firm}
                    />
                )}
            </div>

            {/* {console.log(globTripDetails)} */}

            {showOffcanvasDriver && <div className={showOffcanvasDriver ? "offBack_drop" : ""}></div>}

            <div
                className={`offcanvas offcanvas-end common_offCanvas ${showOffcanvasOwnDriver ? "show" : ""}`}
                tabIndex={-1}
                style={{ visibility: showOffcanvasOwnDriver ? "visible" : "hidden" }}
            >
                {showOffcanvasOwnDriver && (
                    <AddEditOwnDriver
                        isOpen={showOffcanvasOwnDriver}
                        onClose={() => {
                            if (driverModal) {
                                setShowOffcanvasOwnDriver(false);
                                setDriverModalShow(true);
                            } else {
                                setShowOffcanvasOwnDriver(false);
                                setDriverModalShow(false);
                            }
                        }}
                        driverModal={driverModal}
                        callBackDriver={callBackDriver}
                        hideAddNewOption={false}
                        vehicleFirmId={globTripDetails?.interfirm == 0 ? globTripDetails.firm_id : globTripDetails?.vehicle_firm}
                    // vehicleFirmId={globTripDetails?.vehicle_ownership == 2 ? globTripDetails?.firm_id : globTripDetails?.vehicle_firm}
                    />
                )}
            </div>

            {showOffcanvasOwnDriver && <div className={showOffcanvasOwnDriver ? "offBack_drop" : ""}></div>}

            {modalConfig.isOpen && <EbillExtentionModal

                {...ebillExpProps}

            />}


            <style>{`
                .step-status-icon {
                    margin-left: auto;
                    font-size: 20px;
                }
                
                .__simDrverInfo {
                    display: flex;
                    align-items: center;
                    margin-bottom: 15px;
                }
                
                .__simDrverInfo > div {
                    display: flex;
                    flex-direction: column;
                }
                
                .countdown-display {
                    background: #f8f9fa;
                    padding: 10px;
                    border-radius: 6px;
                    border-left: 4px solid #ffc107;
                }
                
                .no-locations {
                    text-align: center;
                    padding: 20px;
                    color: #666;
                    font-style: italic;
                }
                
                .map-loading {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 422px;
                    background: #f8f9fa;
                    color: #666;
                    font-size: 16px;
                }
                
                .anime-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
            `}</style>

        </>
    )
}

export default SimTracking;

